import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@suscri/ui';
import { Plus, Search, Building2, X, Edit, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { PageHeader } from '../components/ui/PageHeader';
import { useAuthenticatedQuery } from '../hooks/useAuthenticatedQuery';
import { trpc } from '../lib/trpc';
import type { RouterOutputs } from '../lib/trpc';

const buildProviderSchema = (t: (k: string) => string) =>
  z.object({
    name: z.string().min(1, t('El nombre es requerido')),
    website: z.string().url(t('URL inválida')).optional().or(z.literal('')),
    notes: z.string().optional(),
  });

type CreateProviderForm = z.infer<ReturnType<typeof buildProviderSchema>>;
type ProviderListItem = RouterOutputs['providers']['list']['data'][number];

export function ProvidersPage() {
  const { t } = useTranslation();
  const createProviderSchema = useMemo(() => buildProviderSchema(t), [t]);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProvider, setEditingProvider] =
    useState<ProviderListItem | null>(null);

  const { enabled } = useAuthenticatedQuery();
  const { data, isLoading, refetch } = trpc.providers.list.useQuery(
    {
      search: search || undefined,
      pagination: { page: 1, limit: 20 },
    },
    {
      enabled,
    }
  );

  const createProviderMutation = trpc.providers.create.useMutation({
    onSuccess: () => {
      setIsModalOpen(false);
      refetch();
    },
  });

  const updateProviderMutation = trpc.providers.update.useMutation({
    onSuccess: () => {
      setIsEditModalOpen(false);
      setEditingProvider(null);
      refetch();
    },
  });

  const deleteProviderMutation = trpc.providers.delete.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateProviderForm>({
    resolver: zodResolver(createProviderSchema),
  });

  const onSubmit = async (data: CreateProviderForm) => {
    const submitData = {
      ...data,
      website: data.website || undefined,
    };
    await createProviderMutation.mutateAsync(submitData);
    reset();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
  };

  const handleEditProvider = (provider: ProviderListItem) => {
    setEditingProvider(provider);
    setIsEditModalOpen(true);
    reset({
      name: provider.name,
      website: provider.website || '',
      notes: provider.notes || '',
    });
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProvider(null);
    reset();
  };

  const handleUpdateProvider = async (data: CreateProviderForm) => {
    if (!editingProvider) return;

    const submitData = {
      ...data,
      website: data.website || undefined,
    };

    await updateProviderMutation.mutateAsync({
      id: editingProvider.id,
      data: submitData,
    });
  };

  const handleDeleteProvider = async (providerId: string) => {
    if (confirm(t('¿Estás seguro de que quieres eliminar este proveedor?'))) {
      await deleteProviderMutation.mutateAsync(providerId);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('Gestión')}
        title={t('Proveedores')}
        description={t('Gestiona los proveedores de tus suscripciones')}
        actions={<Button onClick={() => setIsModalOpen(true)}><Plus className="h-4 w-4" />{t('Nuevo Proveedor')}</Button>}
      />

      <div className="surface-card flex items-center p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('Buscar proveedores...')}
            className="control w-full pl-10 pr-4"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-md" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data?.data.map(provider => (
            <div
              key={provider.id}
              onClick={() => handleEditProvider(provider)}
              className="surface-card-interactive group relative cursor-pointer overflow-hidden p-6"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-md">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {provider.name}
                      </h3>
                      {provider.website && (
                        <a
                          href={provider.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          {provider.website}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEditProvider(provider)}
                      className="p-2 text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      title={t('Editar proveedor')}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProvider(provider.id)}
                      className="p-2 text-gray-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('Eliminar proveedor')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {t('{{count}} suscripciones', {
                    count: provider._count.subscriptions,
                  })}
                </span>
                <span>{new Date(provider.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal para crear proveedor */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseModal}
          />
          <div className="relative w-full max-w-md">
            <div className="overflow-hidden rounded-lg bg-white border border-gray-200 shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('Nuevo Proveedor')}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Nombre *')}
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Ej: Netflix, Spotify, Adobe...')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Sitio Web')}
                  </label>
                  <input
                    {...register('website')}
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://ejemplo.com"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.website.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Notas')}
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t(
                      'Información adicional sobre el proveedor...'
                    )}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {t('Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={createProviderMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {createProviderMutation.isPending
                      ? t('Creando...')
                      : t('Crear Proveedor')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar proveedor */}
      {isEditModalOpen && editingProvider && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleCloseEditModal}
          />
          <div className="relative w-full max-w-md">
            <div className="overflow-hidden rounded-lg bg-white border border-gray-200 shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('Editar Proveedor')}
                </h2>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleSubmit(handleUpdateProvider)}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Nombre *')}
                  </label>
                  <input
                    {...register('name')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('Ej: Netflix, Spotify, Adobe...')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Sitio Web')}
                  </label>
                  <input
                    {...register('website')}
                    type="url"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://ejemplo.com"
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.website.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    {t('Notas')}
                  </label>
                  <textarea
                    {...register('notes')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t(
                      'Información adicional sobre el proveedor...'
                    )}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseEditModal}
                    className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    {t('Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateProviderMutation.isPending}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {updateProviderMutation.isPending
                      ? t('Guardando...')
                      : t('Guardar Cambios')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
